import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Select, Toggle, LoadingSkeleton, Pagination, Modal } from '@nexora/design-system';
import {
  useModuleCtx,
  useRetentionRules,
  useCreateRetentionRule,
  useUpdateRetentionRule,
  useDeleteRetentionRule,
  useToggleRetentionRule,
  type RetentionRule,
  type RetentionRuleInput,
  type RetentionCondition,
  type RetentionConditionField,
  type RetentionConditionOperator,
  type RetentionAction,
  RULE_STATUS_LABEL,
  ACTION_LABEL,
  formatCondition,
  ruleStatusBadgeVariant,
} from './useRetention';
import { formatDateTime } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Configuração de Regras de Retenção
   (protótipo `erpone-configura-o-de-regras-de-reten-o`)
   ───────────────────────────────────────────────────────────────────────────
   Contratos consumidos (backend real, DTOs em retention.dto.ts):
   - GET  /retention/rules      → PaginatedRetentionRuleResponseDto
   - POST /retention/rules      → RetentionRuleDto (CreateRetentionRuleDto)
   - PATCH/DELETE /retention/rules/:id
   - POST /retention/rules/:id/activate | deactivate → RetentionRuleDto

   Tabela: Prioridade · Condição (Se) · Ação (Então) · Status · Ações.
   Condição = { campo, operador, valor } — formatada como texto legível.
   ═══════════════════════════════════════════════════════════════════════════ */

interface ToastState {
  message: string;
  variant: 'success' | 'error';
}

const FIELD_OPTIONS: { value: RetentionConditionField; label: string }[] = [
  { value: 'valorBruto', label: 'Valor Bruto' },
  { value: 'tipoServico', label: 'Tipo de Serviço' },
  { value: 'prestadorCnpj', label: 'CNPJ do Prestador' },
  { value: 'prestadorRazaoSocial', label: 'Razão Social do Prestador' },
  { value: 'uf', label: 'UF' },
  { value: 'municipio', label: 'Município' },
];

const OPERATOR_OPTIONS: { value: RetentionConditionOperator; label: string }[] = [
  { value: '>', label: '>' },
  { value: '>=', label: '≥' },
  { value: '<', label: '<' },
  { value: '<=', label: '≤' },
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
  { value: 'EM', label: 'EM' },
];

const ACTION_OPTIONS: { value: RetentionAction; label: string }[] = [
  { value: 'RETER_ISS', label: 'Reter ISS' },
  { value: 'RETER_IRRF', label: 'Reter IRRF' },
  { value: 'RETER_ISS_IRRF', label: 'Reter ISS + IRRF' },
  { value: 'NAO_RETER', label: 'Não Reter' },
];

/* ── Modal: Criar / Editar regra ── */

function RuleFormModal({
  isOpen,
  onClose,
  rule,
  existingRules,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  rule?: RetentionRule;
  existingRules: RetentionRule[];
  onSubmit: (data: RetentionRuleInput) => Promise<void>;
}): JSX.Element {
  const [prioridade, setPrioridade] = useState('1');
  const [campo, setCampo] = useState<RetentionConditionField>('valorBruto');
  const [operador, setOperador] = useState<RetentionConditionOperator>('>=');
  const [valor, setValor] = useState('');
  const [acao, setAcao] = useState<RetentionAction>('RETER_ISS');
  const [descricao, setDescricao] = useState('');
  const [retencaoNaFonte, setRetencaoNaFonte] = useState(true);
  const [autoLancamento, setAutoLancamento] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (rule) {
      setPrioridade(String(rule.prioridade));
      setCampo(rule.condicao.campo);
      setOperador(rule.condicao.operador);
      setValor(Array.isArray(rule.condicao.valor) ? rule.condicao.valor.join(', ') : String(rule.condicao.valor));
      setAcao(rule.acao);
      setDescricao(rule.descricao ?? '');
      setRetencaoNaFonte(rule.retencaoNaFonte);
      setAutoLancamento(rule.autoLancamento);
    } else {
      setPrioridade(String((existingRules.length > 0 ? Math.max(...existingRules.map((r) => r.prioridade)) : 0) + 1));
      setCampo('valorBruto');
      setOperador('>=');
      setValor('');
      setAcao('RETER_ISS');
      setDescricao('');
      setRetencaoNaFonte(true);
      setAutoLancamento(false);
    }
    setError(null);
  }, [rule, isOpen, existingRules]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const prioridadeNum = Number(prioridade);
    if (!Number.isFinite(prioridadeNum) || prioridadeNum < 1) {
      setError('Prioridade deve ser um número maior ou igual a 1.');
      return;
    }
    if (valor.trim() === '') {
      setError('Valor da condição é obrigatório.');
      return;
    }
    const condicao: RetentionCondition = {
      campo,
      operador,
      valor: operador === 'EM' ? valor.split(',').map((v) => v.trim()).filter(Boolean)
        : campo === 'valorBruto' ? Number(valor)
        : valor.trim(),
    };
    if (campo === 'valorBruto' && typeof condicao.valor === 'number' && !Number.isFinite(condicao.valor)) {
      setError('Valor Bruto deve ser numérico.');
      return;
    }
    if (Array.isArray(condicao.valor) && condicao.valor.length === 0) {
      setError('Informe ao menos um valor para a condição EM.');
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit({
        prioridade: prioridadeNum,
        condicao,
        acao,
        descricao: descricao.trim() || undefined,
        retencaoNaFonte,
        autoLancamento,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar regra.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={rule ? 'Editar Regra de Retenção' : 'Nova Regra Global'} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
            <Input label="Prioridade (1 = maior)" type="number" value={prioridade} onChange={(e) => setPrioridade(e.target.value)} required min={1} />
            <Input label="Descrição (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: Retém ISS acima de R$ 100 para construção civil" />
          </div>
          <div style={{ background: '#f6f8fa', border: '1px solid #e0e3e5', borderRadius: '8px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ background: '#d6e3ff', color: '#002045', fontWeight: 700, padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                SE
              </span>
              <span style={{ fontSize: '0.875rem', color: '#43474e' }}>Condição da regra</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 140px' }}>
                <Select label="Campo" value={campo} onChange={(e) => setCampo(e.target.value as RetentionConditionField)}>
                  {FIELD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: '0 0 90px' }}>
                <Select label="Operador" value={operador} onChange={(e) => setOperador(e.target.value as RetentionConditionOperator)}>
                  {OPERATOR_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <Input
                  label={operador === 'EM' ? 'Valores (separados por vírgula)' : 'Valor'}
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  required
                  placeholder={campo === 'valorBruto' ? 'Ex.: 100' : 'Ex.: CONSTRUCAO_CIVIL'}
                />
              </div>
            </div>
          </div>
          <div style={{ background: '#f6f8fa', border: '1px solid #e0e3e5', borderRadius: '8px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ background: '#d6e3ff', color: '#002045', fontWeight: 700, padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                ENTÃO
              </span>
              <span style={{ fontSize: '0.875rem', color: '#43474e' }}>Ação da regra</span>
            </div>
            <Select label="Ação" value={acao} onChange={(e) => setAcao(e.target.value as RetentionAction)}>
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <Toggle checked={retencaoNaFonte} onChange={setRetencaoNaFonte} label="Retenção na fonte" />
            <Toggle checked={autoLancamento} onChange={setAutoLancamento} label="Auto-lançamento" />
          </div>
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : rule ? 'Salvar alterações' : 'Salvar Regra'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ── Página ── */

export function RetentionRulesPage(): JSX.Element {
  const ctx = useModuleCtx();
  const { data, isLoading, error } = useRetentionRules(ctx, { limit: 50 });
  const createMutation = useCreateRetentionRule(ctx);
  const updateMutation = useUpdateRetentionRule();
  const deleteMutation = useDeleteRetentionRule(ctx);
  const toggleMutation = useToggleRetentionRule(ctx);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RetentionRule | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const handleCreate = useCallback(
    async (input: RetentionRuleInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Regra criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar regra.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: RetentionRuleInput) => {
      if (!editingRule) return;
      try {
        await updateMutation.mutateAsync({ id: editingRule.id, ...input });
        showToast('Regra atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar regra.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingRule, updateMutation, showToast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Tem certeza que deseja excluir esta regra?')) return;
      try {
        await deleteMutation.mutateAsync(id);
        showToast('Regra excluída com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir regra.';
        showToast(msg, 'error');
      }
    },
    [deleteMutation, showToast],
  );

  const handleToggle = useCallback(
    async (rule: RetentionRule) => {
      const target = rule.status === 'ATIVA' ? 'deactivate' : 'activate';
      const label = target === 'activate' ? 'ativada' : 'inativada';
      try {
        await toggleMutation.mutateAsync({ id: rule.id, transition: target });
        showToast(`Regra ${label} com sucesso.`, 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao alterar status da regra.';
        showToast(msg, 'error');
      }
    },
    [toggleMutation, showToast],
  );

  const rules = (data?.data ?? []).filter((r) => statusFilter === 'ALL' || r.status === statusFilter);

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>Configuração de Regras de Retenção</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Gerencie parâmetros e exceções para o cálculo de tributos retidos na fonte — {data?.total ?? 0} regras.
          </p>
        </div>
        <Button variant="primary" onClick={() => { setEditingRule(undefined); setModalOpen(true); }}>+ Nova Regra Global</Button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Regras ativas', value: String((data?.data ?? []).filter((r) => r.status === 'ATIVA').length) },
          { label: 'Regras inativas', value: String((data?.data ?? []).filter((r) => r.status === 'INATIVA').length) },
          { label: 'Com auto-lançamento', value: String((data?.data ?? []).filter((r) => r.autoLancamento).length) },
          { label: 'Com retenção na fonte', value: String((data?.data ?? []).filter((r) => r.retencaoNaFonte).length) },
        ].map((s) => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>{s.label}</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#191c1e' }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#191c1e' }}>Regras de Retenção</h2>
        <div style={{ width: '200px' }}>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">Todos</option>
            <option value="ATIVA">Ativas</option>
            <option value="INATIVA">Inativas</option>
          </Select>
        </div>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Regras (Se → Então)</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando regras...</span>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar regras</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>Tentar novamente</Button>
          </div>
        )}
        {!isLoading && !error && rules.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhuma regra cadastrada.</p>
            <Button variant="outline" size="sm" onClick={() => { setEditingRule(undefined); setModalOpen(true); }}>Criar primeira regra</Button>
          </div>
        )}
        {!isLoading && !error && rules.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Prioridade', 'Condição (Se)', 'Ação (Então)', 'Status', 'Atualizado em', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules
                  .slice()
                  .sort((a, b) => a.prioridade - b.prioridade)
                  .map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #eceef0' }}>
                      <td style={{ padding: '0.625rem 1rem', fontWeight: 700 }}>{r.prioridade}</td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <div style={{ fontWeight: 500 }}>{formatCondition(r.condicao)}</div>
                        {r.descricao && (
                          <div style={{ fontSize: '0.75rem', color: '#74777f' }}>{r.descricao}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <Badge variant={r.acao === 'NAO_RETER' ? 'neutral' : 'info'} size="sm">{ACTION_LABEL[r.acao]}</Badge>
                      </td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <Badge variant={ruleStatusBadgeVariant(r.status)} size="sm">{RULE_STATUS_LABEL[r.status]}</Badge>
                      </td>
                      <td style={{ padding: '0.625rem 1rem', color: '#74777f', whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                        {formatDateTime(r.updatedAt)}
                      </td>
                      <td style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingRule(r); setModalOpen(true); }}>Editar</Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(r)}>
                          {r.status === 'ATIVA' ? 'Inativar' : 'Ativar'}
                        </Button>
                        <Button variant="ghost" size="sm" style={{ color: '#ba1a1a' }} onClick={() => handleDelete(r.id)}>Excluir</Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && (data?.totalPages ?? 1) > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={data?.page ?? 1} totalPages={data?.totalPages ?? 1} onChange={() => {}} />
          </div>
        )}
      </Card>

      <RuleFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        rule={editingRule}
        existingRules={data?.data ?? []}
        onSubmit={editingRule ? handleUpdate : handleCreate}
      />

      {toast && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
          }}
        >
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Fechar" style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}>
            &times;
          </button>
        </div>
      )}

      <style>{`.module-page { padding: 1.5rem; }`}</style>
    </div>
  );
}